import { collection, deleteDoc, deleteField, doc, getCountFromServer, getDoc, getDocs, getFirestore, limit, orderBy, query, startAfter, updateDoc, where } from "firebase/firestore";
import type { FieldValue } from "firebase/firestore";
import type { QueryDocumentSnapshot } from "firebase/firestore";
import { toast } from "react-toastify";
import { database, functions } from "../..";
import { handleAuthError, isAuthError } from "../../../utils/handleAuthError";
import type Fee from "../../../interfaces/Fee";
import type UserRole from "../../../interfaces/UserRole";
import { httpsCallable } from "firebase/functions";

/** Referencia ligera a otro usuario (vendedor o referidor). */
export interface Referente {
  id: string;
  name: string;
}

export type EstadoUsuario = "Pendiente" | "Confirmado";

/** Origen del alta del usuario. Solo "web" cuenta contra agencia.webUserLimit. */
export type OrigenUsuario = "web";

/** Marca de origen que escribe la función createUserByAdmin. */
export const ORIGEN_WEB: OrigenUsuario = "web";

export interface Usuario {
  id: string;
  countryCode: string;
  email: string;
  lastName: string;
  lockerCode: string;
  name: string;
  phone: string;
  token?: string;
  puntos?: number;
  tarifaAerea?: Fee;
  tarifaMaritima?: Fee;
  role?: UserRole;
  // Estado de aprobación de la cuenta (alta desde la app móvil).
  estado?: EstadoUsuario;
  // Staff responsable que trajo/registró al cliente (para métricas).
  vendedor?: Referente;
  // Usuario que refirió a este cliente (para el plan de referidos).
  referidoPor?: Referente;
  // Origen del alta: "web" si se creó desde el panel de administración.
  origen?: OrigenUsuario;
}

export type UsuarioConStats = Usuario & {
  packages: number;
  total: number;
  aereo: number;
  maritimo: number;
};

export interface TopUsuarios extends Usuario {
  packages: number;
  total: number;
  aereo: number;
  maritimo: number;
}

type ConteoPorUsuario = Record<
  string,
  {
    totalPackages: number;
    total: number;
    aereo: number;
    maritimo: number;
  }
>;

function normalizeSearchValue(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase().replace(/\s+/g, " ");
}

function matchesUserSearchTerms(
  usuario: Partial<Usuario>,
  searchTerms: string[]
): boolean {
  // Todo normalizado a minúsculas: la búsqueda es indistinta de may/min.
  const nombreTokens = normalizeSearchValue(
    [usuario.name, usuario.lastName].filter(Boolean).join(" ")
  )
    .split(" ")
    .filter(Boolean);
  const locker = normalizeSearchValue(usuario.lockerCode);

  // Cada término debe ser prefijo de un token del nombre/apellido, o estar
  // contenido en el código de casillero (id del cliente).
  return searchTerms.every(
    (term) =>
      nombreTokens.some((token) => token.startsWith(term)) ||
      locker.includes(term)
  );
}

/**
 * Genera variantes de mayúsculas/minúsculas de un prefijo. Firestore no
 * soporta consultas insensibles a may/min, por lo que se consulta cada forma
 * común en que puede estar almacenado el dato: minúscula, MAYÚSCULA y
 * Capitalizada.
 */
function caseVariants(prefijo: string): string[] {
  const lower = prefijo.toLowerCase();
  const upper = prefijo.toUpperCase();
  const capitalized = lower.charAt(0).toUpperCase() + lower.slice(1);
  return Array.from(new Set([lower, upper, capitalized]));
}

/**
 * Busca usuarios por nombre, apellido, nombre completo o c\u00f3digo de casillero
 * (id del cliente). La b\u00fasqueda es indistinta de may\u00fasculas/min\u00fasculas: se
 * consultan las variantes de case del prefijo de anclaje y luego se refina en
 * cliente.
 */
export async function searchUsersByFullNameOrLocker(valor: string) {
  const usuariosRef = collection(database, "users");
  const searchTerms = normalizeSearchValue(valor).split(" ").filter(Boolean);
  if (searchTerms.length === 0) return [];

  // El primer t\u00e9rmino ancla las consultas por rango (prefijo).
  const variantes = caseVariants(searchTerms[0]);
  const campos = ["name", "lastName", "lockerCode"] as const;

  const snapshots = await Promise.all(
    campos.flatMap((campo) =>
      variantes.map((prefijo) =>
        getDocs(
          query(
            usuariosRef,
            where(campo, ">=", prefijo),
            where(campo, "<=", prefijo + "\uf8ff")
          )
        )
      )
    )
  );

  const usuariosPorId = new Map<string, Usuario>();
  snapshots.forEach((snap) =>
    snap.docs.forEach((doc) => {
      if (!usuariosPorId.has(doc.id)) {
        usuariosPorId.set(doc.id, {
          id: doc.id,
          ...(doc.data() as Partial<Usuario>),
        } as Usuario);
      }
    })
  );

  // Refinamiento final insensible a may/min sobre los candidatos.
  return Array.from(usuariosPorId.values()).filter((user) =>
    matchesUserSearchTerms(user, searchTerms)
  );
}

export async function fetchUsuariosLoteado(
  startDate: Date,
  endDate: Date,
  lastDoc?: QueryDocumentSnapshot,
): Promise<{
  usuarios: UsuarioConStats[];
  lastDoc?: QueryDocumentSnapshot;
}> {
  const usuariosRef = collection(database, "users");

  const usuariosQuery = query(
    usuariosRef,
    orderBy("lockerCode", "desc"),
    ...(lastDoc ? [startAfter(lastDoc)] : []),
    limit(100)
  );

  const snapshot = await getDocs(usuariosQuery);

  const usuariosRaw = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...(doc.data() as Partial<Usuario>),
  })) as Usuario[];

  const usuariosConStats: UsuarioConStats[] = await Promise.all(
    usuariosRaw.map(async (usuario) => {
      const paquetesRef = collection(database, "paquetes");

      const q = query(
        paquetesRef,
        where("idUsuario", "==", usuario.id),
        where("createdAt", ">=", startDate),
        where("createdAt", "<=", endDate)
      );

      const paquetesSnap = await getDocs(q);

      let total = 0;
      let aereo = 0;
      let maritimo = 0;

      paquetesSnap.forEach((p) => {
        const { via, total: paqueteTotal } = p.data();

        if (paqueteTotal) {
          total += paqueteTotal;
        }

        if (via === "aereo") aereo++;
        else if (via === "maritimo") maritimo++;
      });

      total = Math.round(total * 100) / 100;

      return {
        ...usuario,
        packages: paquetesSnap.size,
        total,
        aereo,
        maritimo,
      };
    })
  );

  const lastVisible = snapshot.docs[snapshot.docs.length - 1];

  return {
    usuarios: usuariosConStats,
    lastDoc: lastVisible
  };
}

export async function updateUsuario(id: string, usuario: Partial<Usuario>) {
  try {
    const docRef = doc(database, "users", id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const updateData: Record<string, FieldValue | string | number | boolean | Fee | UserRole | Referente | null> = {};
      
      // Actualizar solo los campos que vienen en usuario
      Object.keys(usuario).forEach((key) => {
        const value = usuario[key as keyof Usuario];
        if (value === undefined) {
          // Si el valor es undefined, eliminar el campo de Firestore usando deleteField
          updateData[key] = deleteField();
        } else {
          // Si tiene valor, actualizarlo
          updateData[key] = value as string | number | boolean | Fee | UserRole | Referente | null;
        }
      });
      
      // Usar updateDoc en lugar de setDoc para poder usar deleteField()
      await updateDoc(docRef, updateData);
      toast.success("Usuario actualizado con éxito");
      return null;
    }
  } catch (error) {
    if (isAuthError(error)) {
      handleAuthError();
      return null;
    }
    console.error("Error actualizando usuario: ", error);
    toast.error("Ocurrio un error al actualizar usuario");
    return null;
  }
}

export async function countUsuarios() {
  const usuariosRef = collection(database, "users");

  const queryUsuarios = query(usuariosRef);
  const snapshot = await getCountFromServer(queryUsuarios);
  const total = snapshot.data().count;

  return total
}

/**
 * Cuenta los usuarios creados desde la web (los que consumen webUserLimit).
 * Las cuentas registradas desde la app móvil no llevan `origen` y no cuentan.
 */
export async function countUsuariosWeb() {
  const usuariosRef = collection(database, "users");

  const queryUsuarios = query(usuariosRef, where("origen", "==", ORIGEN_WEB));
  const snapshot = await getCountFromServer(queryUsuarios);

  return snapshot.data().count;
}

export async function deleteUsuario(userId: string) {
  try {
    // Verificar que el usuario existe en Firestore
    const docRef = doc(database, "users", userId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error("Usuario no encontrado en Firestore");
    }

    // Primero eliminar de Firebase Authentication usando Cloud Function
    const deleteUserFunction = httpsCallable(functions, "deleteUser");
    await deleteUserFunction({ uid: userId });

    // Luego eliminar documento de Firestore
    await deleteDoc(docRef);

    toast.success("Usuario eliminado exitosamente");
  } catch (error) {
    if (isAuthError(error)) {
      handleAuthError();
      throw error;
    }
    console.error("Error eliminando usuario: ", error);
    toast.error("Ocurrió un error al eliminar el usuario");
    throw error;
  }
}

export interface VendedorStats {
  clientes: number;
  packages: number;
  total: number;
  aereo: number;
  maritimo: number;
}

/**
 * Devuelve los usuarios del staff (Admin y Digitador) para usarlos como
 * opciones de "Vendedor" en el módulo de estadísticas.
 */
export async function fetchStaffUsuarios(): Promise<Usuario[]> {
  try {
    const usuariosRef = collection(database, "users");
    const q = query(usuariosRef, where("role", "in", ["Admin", "Digitador"]));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...(doc.data() as Partial<Usuario>),
    })) as Usuario[];
  } catch (error) {
    if (isAuthError(error)) {
      handleAuthError();
      return [];
    }
    console.error("Error obteniendo staff: ", error);
    return [];
  }
}

/**
 * Agrega los paquetes (conteo y dinero) de todos los clientes cuyo
 * `vendedor.id` coincide con el vendedor indicado, dentro del rango de fechas.
 */
export async function getStatsByVendedor(
  vendedorId: string,
  startDate: Date,
  endDate: Date
): Promise<VendedorStats> {
  const usuariosRef = collection(database, "users");
  const clientesSnap = await getDocs(
    query(usuariosRef, where("vendedor.id", "==", vendedorId))
  );
  const clienteIds = clientesSnap.docs.map((d) => d.id);

  let packages = 0;
  let total = 0;
  let aereo = 0;
  let maritimo = 0;

  await Promise.all(
    clienteIds.map(async (cid) => {
      const paquetesRef = collection(database, "paquetes");
      const pq = query(
        paquetesRef,
        where("idUsuario", "==", cid),
        where("createdAt", ">=", startDate),
        where("createdAt", "<=", endDate)
      );
      const psnap = await getDocs(pq);
      psnap.forEach((p) => {
        const { via, total: paqueteTotal } = p.data();
        packages += 1;
        if (paqueteTotal) total += paqueteTotal;
        if (via === "aereo") aereo += 1;
        else if (via === "maritimo") maritimo += 1;
      });
    })
  );

  total = Math.round(total * 100) / 100;

  return { clientes: clienteIds.length, packages, total, aereo, maritimo };
}

export async function fetchTopUsuariosByPaquetes(startDate: Date, endDate: Date) {
  const db = getFirestore();
  const paquetesRef = collection(db, "paquetes");

  const q = query(
    paquetesRef,
    where("createdAt", ">=", startDate),
    where("createdAt", "<=", endDate)
  );

  const snapshot = await getDocs(q);

  const conteo: ConteoPorUsuario = {};

  snapshot.forEach((doc) => {
    const data = doc.data();
    const idUsuario = data.idUsuario;
    const { via, total: paqueteTotal } = data;

    if (!idUsuario || !via) return;

    if (!conteo[idUsuario]) {
      conteo[idUsuario] = { totalPackages: 0, total: 0, aereo: 0, maritimo: 0 };
    }

    conteo[idUsuario].totalPackages += 1;

    if (paqueteTotal) {
      conteo[idUsuario].total += paqueteTotal;
    }

    if (via === "aereo") conteo[idUsuario].aereo += 1;
    else if (via === "maritimo") conteo[idUsuario].maritimo += 1;
  });

  const top3 = Object.entries(conteo)
    .sort((a, b) => b[1].total - a[1].total)
    .slice(0, 3);

  const topUsuarios: TopUsuarios[] = await Promise.all(
    top3.map(async ([usuarioId, { totalPackages, total, aereo, maritimo }]) => {
      const docRef = doc(database, "users", usuarioId);
      const docSnap = await getDoc(docRef);
      const usuario = docSnap.data();

      return {
        ...usuario,
        packages: totalPackages,
        total: Math.round(total * 100) / 100,
        aereo,
        maritimo,
      } as TopUsuarios;
    })
  );

  return topUsuarios as TopUsuarios[];
}